import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewChild,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { Editor, EditorModule } from 'primeng/editor';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';
import { MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { TieredMenuModule } from 'primeng/tieredmenu';

interface EditorFieldProps extends FormlyFieldProps {
  height?: string;
  variables?: boolean;
  preview?: any;
  viewTextEditors?: boolean | undefined;
}

@Component({
  selector: 'editor-field',
  standalone: true,
  imports: [...FORM_IMPORTS, EditorModule, TieredMenuModule],
  template: `
    <p-editor
      #editor
      [formControl]="formControl"
      [formlyAttributes]="field"
      class="w-full"
      [style]="{ height: props.height ?? '120px' }"
    >
      <ng-template #header>
        @if (props.viewTextEditors || props.viewTextEditors === undefined) {
          <span class="ql-formats">
            <select class="ql-size"></select>
          </span>

          <span class="ql-formats">
            <button class="ql-bold"></button>
            <button class="ql-italic"></button>
            <button class="ql-underline"></button>
          </span>
          <span class="ql-formats">
            <select class="ql-color"></select>
          </span>
          <span class="ql-formats">
            <button class="ql-list" value="ordered"></button>
            <button class="ql-list" value="bullet"></button>
          </span>
          <span class="ql-formats">
            <select class="ql-align"></select>
          </span>
          <span class="ql-formats">
            <button class="ql-clean"></button>
          </span>
          <span class="ql-formats">
            <button class="ql-link"></button>
            <button class="ql-image"></button>
          </span>
        }

        @if (props.variables) {
          <span class="ql-formats" #variables>
            <button
              (click)="menu.toggle($event)"
              type="button"
              style="padding-block:0; padding-inline:0;"
            >
              <i
                class="fa-solid fa-code text-sm text-[var(--p-text-muted-color)] hover:text-[var(--p-text-color)]"
              ></i>
            </button>
            <p-tieredmenu
              #menu
              [model]="items"
              [popup]="true"
              [appendTo]="'body'"
              styleClass="absolute"
            />
          </span>
        }

        @if (props.preview) {
          <span class="ql-formats">
            <button
              (click)="props.preview(this.formControl)"
              type="button"
              style="padding-block:0; padding-inline:0;"
            >
              <i
                class="fa-solid fa-eye text-sm text-[var(--p-text-muted-color)] hover:text-[var(--p-text-color)]"
              ></i>
            </button>
          </span>
        }
      </ng-template>
    </p-editor>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorFieldType extends FieldType<
  FieldTypeConfig<EditorFieldProps>
> {
  @ViewChild('editor') editorComponent!: Editor;
  private readonly _translate = inject(TranslateService);

  items: MenuItem[] | undefined;

  ngAfterViewInit() {
    const variableGroups = [
      {
        label: this._translate.instant('formly.type.editor.info'),
        variables: [
          '{{sottoscrivente}}',
          '{{firma}}',
          '{{data_ora}}',
          '{{data_scadenza}}',
          '{{tipologia_richiesta}}',
          '{{iban_accredito_incentivi}}',
          '{{intestatario_conto_corrente}}',
          '{{destinazione_incentivi}}',
          '{{{elenco_allegati}}}',
          '{{{elenco_domande}}}',
        ],
      },
      {
        label: this._translate.instant('formly.type.editor.cer'),
        variables: [
          '{{cer}}',
          '{{indirizzo_cer}}',
          '{{contatti_cer}}',
          '{{codice_fiscale_cer}}',
          '{{partita_iva_cer}}',
          '{{configurazione}}',
        ],
      },
      {
        label: this._translate.instant('formly.type.editor.utenza'),
        variables: ['{{tipologia_utenza}}', '{{indirizzo_utenza}}'],
      },
      {
        label: this._translate.instant('formly.type.editor.consumer'),
        variables: ['{{pod_consumo}}', '{{consumo}}'],
      },
      {
        label: this._translate.instant('formly.type.editor.prosumer'),
        variables: [
          '{{potenza_effettiva}}',
          '{{potenza_batteria}}',
          '{{data_installazione_impianto}}',
        ],
      },
      {
        label: this._translate.instant('formly.type.editor.future'),
        variables: [
          '{{potenza_impianto_prevista}}',
          '{{capacita_batteria_prevista}}',
          '{{data_allaccio_prevista}}',
        ],
      },
    ];

    this.items = variableGroups.map((group) => ({
      label: group.label,
      items: group.variables.map((label) => ({
        label,
        command: () => this.insertVariable(label),
      })),
    }));
  }

  private insertVariable(text: string) {
    const quill = this.editorComponent?.quill;

    if (quill) {
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, text);
        quill.setSelection(range.index + text.length);
      } else {
        const length = quill.getLength();
        quill.insertText(length - 1, text);
        quill.setSelection(length - 1 + text.length);
      }

      // ðŸ”„ Forza l'aggiornamento del formControl
      const html = this.editorComponent?.getQuill().root.innerHTML;
      this.formControl.setValue(html);
      this.formControl.markAsDirty(); // Indica che il valore Ã¨ stato modificato
      this.formControl.markAsTouched();
    }
  }
}
